/**
 * FolderRow — renders a single folder as a table row in the Drive content area.
 *
 * Props:
 *   folder {object} — shape: { id, name, parent_id, has_children, virtual, path, created_at }
 *
 * Virtual folders (tasks, equipments hierarchy) use folder.path for SPA navigation instead of
 * the default /drive/:id URL.
 */

import { h } from 'preact'
import { useContext } from 'preact/hooks'
import { DriveStoreContext, DriveI18nContext, DriveUiContext } from './drive-context.js'
import { UploaderCell } from './UploaderCell.jsx'
import { RowActions } from './RowActions.jsx'

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return iso
  }
}

export function FolderRow({ folder, selectedIds = [] }) {
  const store      = useContext(DriveStoreContext)
  const i18n       = useContext(DriveI18nContext)
  const ui         = useContext(DriveUiContext)
  const t          = (key) => i18n ? i18n.t(key) : key
  const itemId     = `folder-${folder.id}`
  const isSelected = selectedIds.includes(itemId)

  function handleNameClick(e) {
    e.preventDefault()
    e.stopPropagation()
    const path = folder.virtual && folder.path ? folder.path : `/drive/${folder.id}`
    history.pushState({}, '', path)
    store.loadFolder(`${path}.json`)
  }

  function handleRowClick(e) {
    if (e.target.closest('a, button, .drive-item-thumb, .dropdown-menu')) return
    store.selectItem(itemId, e.metaKey || e.ctrlKey)
  }

  function handleThumbSelect(e) {
    e.preventDefault()
    e.stopPropagation()
    store.selectItem(itemId, true)
  }

  const isVirtual = folder.virtual === true
  const folderIcon = folder.has_children ? 'bi-folder-fill text-warning' : 'bi-folder text-warning'
  const folderIdParam = { 'data-drive-folder-id-param': folder.id }
  const actions = isVirtual ? [] : [
    {
      key: 'download',
      label: t('drive.download_folder_title'),
      icon: 'bi-download',
      buttonClass: 'btn-outline-secondary',
      dataAction: 'click->drive#downloadFolder',
      dataParams: folderIdParam,
    },
    {
      key: 'share',
      label: t('drive.share_this'),
      icon: 'bi-share',
      buttonClass: 'btn-outline-info',
      dataAction: 'click->drive#shareFolder',
      dataParams: folderIdParam,
    },
    {
      key: 'permissions',
      label: t('drive.manage_permissions_title'),
      icon: 'bi-shield-lock',
      buttonClass: 'btn-outline-secondary',
      dataAction: 'click->drive#manageFolderPermissions',
      dataParams: folderIdParam,
    },
    {
      key: 'delete',
      label: t('drive.delete_item'),
      icon: 'bi-trash',
      buttonClass: 'btn-outline-danger',
      danger: true,
      dataAction: 'click->drive#deleteFolder',
      dataParams: folderIdParam,
    },
  ]

  const menuActions = (!isVirtual && ui?.canUpdate) ? [
    {
      key: 'rename',
      label: t('drive.rename'),
      icon: 'bi-pencil',
      onClick: () => ui.openRename({ type: 'folder', id: folder.id, name: folder.name }),
    },
    {
      key: 'move',
      label: t('drive.move'),
      icon: 'bi-folder-symlink',
      onClick: () => ui.requestMove(itemId),
    },
  ] : []

  return (
    <tr
      class={isSelected ? 'table-active' : ''}
      aria-selected={isSelected}
      data-item-id={folder.id}
      data-item-type="folder"
      tabindex="0"
      onClick={handleRowClick}
      style="cursor: pointer;"
    >
      <td class="text-center align-middle">
        {isVirtual ? (
          <i class={`bi ${folderIcon}`}></i>
        ) : (
          <div
            class={`drive-item-thumb${isSelected ? ' is-selected' : ''}`}
            role="presentation"
          >
            <span
              class="drive-item-thumb__preview"
              aria-hidden="true"
              onClick={handleThumbSelect}
            >
              <i class={`bi ${folderIcon}`}></i>
            </span>
            <label
              class="drive-item-thumb__check"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isSelected}
                aria-label={t('drive.select_item')}
                onClick={(e) => e.stopPropagation()}
                onChange={handleThumbSelect}
              />
            </label>
          </div>
        )}
      </td>
      <td class="align-middle">
        <a
          href={folder.virtual && folder.path ? folder.path : `/drive/${folder.id}`}
          class="text-decoration-none fw-medium"
          onClick={handleNameClick}
        >
          {folder.name}
        </a>
      </td>
      {isVirtual ? (
        <td class="d-none d-md-table-cell align-middle text-muted">
          <small>—</small>
        </td>
      ) : (
        <UploaderCell person={folder.uploaded_by} />
      )}
      <td class="d-none d-md-table-cell align-middle text-muted">
        <small>—</small>
      </td>
      <td class="d-none d-md-table-cell align-middle text-muted">
        <small>{isVirtual ? '—' : formatDate(folder.created_at)}</small>
      </td>
      {isVirtual ? <td></td> : <RowActions actions={actions} menuActions={menuActions} />}
    </tr>
  )
}
